import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from 'mobx';

import { LoadStateStore } from 'app/loading/LoadStateStore';
import { OrderService } from 'app/service/OrderService';
import { CardOrder } from 'app/model/CardOrder';
import { DocumentReference } from 'app/model/DocumentReference';
import { ProductService } from 'app/service/ProductService';
import { CardProduct } from 'app/model/product/CardProduct';
import { ReferenceService } from 'app/service/ReferenceService';
import { Reference } from 'app/model/Reference';
import { OtpConfirmation } from 'app/model/OtpConfirmation';
import { ResourceFormat } from 'app/model/ResourceFormat';
import { ConfirmationFormModel } from 'app/logic/pages/confirmation/ConfirmationFormModel';

/**
 * Хранилище данных страницы {@link ConfirmationPage}
 */
export class ConfirmationPageStore {
    private readonly _loadStateStore: LoadStateStore = new LoadStateStore();

    readonly actionStateStore: LoadStateStore = new LoadStateStore(false);

    readonly downloadAllStateStore: LoadStateStore = new LoadStateStore(true);

    readonly confirmationFormModel: ConfirmationFormModel =
        new ConfirmationFormModel();

    private readonly orderService: OrderService = new OrderService();

    private readonly productService: ProductService = new ProductService();

    private readonly referenceService: ReferenceService =
        new ReferenceService();

    @observable
    private _order: CardOrder;

    @observable
    private _resources: DocumentReference[];

    @observable
    private _cardProduct: CardProduct;

    private _country: Reference;

    private _streetType: Reference;

    private _localityType: Reference;

    @observable
    private _otpConfirmation: OtpConfirmation;

    @observable
    conditionAgreement: boolean = false;

    constructor() {
        makeObservable(this);
    }

    @action
    async initialize(id: string) {
        this.loadStateStore.loadData(async () => {
            const order = await this.orderService.getById(id);

            const [cardProduct, resources, country, streetType, localityType] =
                await Promise.all([
                    this.productService.getCardProduct(
                        order.cardProductCode,
                        order.saleChannel,
                    ),
                    this.orderService.getOrderDocuments(id),
                    this.referenceService.getCountryById(
                        order.deliveryAddress.countryCode,
                    ),
                    this.referenceService.getStreetTypeById(
                        order.deliveryAddress.streetType,
                    ),
                    this.referenceService.getLocalityTypeById(
                        order.deliveryAddress.localityType,
                    ),
                ]);

            this._country = country;
            this._streetType = streetType;
            this._localityType = localityType;

            runInAction(() => {
                this.setOrder(order);
                this.setResources(resources);
                this.setCardProduct(cardProduct);
                this.confirmationFormModel.init(resources);
            });
        });
    }

    async downloadAll() {
        return this.downloadAllStateStore.loadData(() => {
            return this.orderService.getZipArchive(this._order.id);
        });
    }

    get country(): Reference {
        return this._country;
    }

    get streetType(): Reference {
        return this._streetType;
    }

    get localityType(): Reference {
        return this._localityType;
    }

    @computed
    get order() {
        return this._order;
    }

    @computed
    get resources() {
        return this._resources;
    }

    @computed
    get cardProduct() {
        return this._cardProduct;
    }

    @action
    setOrder(order: CardOrder) {
        this._order = order;
    }

    @action
    setResources(resources: DocumentReference[]) {
        this._resources = resources;
    }

    @action
    setCardProduct(cardProduct: CardProduct) {
        this._cardProduct = cardProduct;
    }

    @computed
    get loadStateStore() {
        return this._loadStateStore;
    }

    @computed
    get otpConfirmation(): OtpConfirmation {
        return this._otpConfirmation;
    }

    async getDocumentResource(
        orderId: string,
        code: string,
        format: ResourceFormat,
    ) {
        return this.orderService.getDocumentResource(orderId, code, format);
    }
}
